import { Controller } from "@hotwired/stimulus"
import * as pdfjsLib from "pdfjs-dist"

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs"

const THUMB_WIDTH = 128   // css px per thumbnail
const PREVIEW_MAX = 900   // css px cap for the large preview render

// Page picker for multi-page PDF floorplans (TASK-85, thumbnails TASK-86).
// When the chosen file is a PDF with more than one page, renders a thumbnail
// card per page: the corner tick toggles selection, clicking the thumbnail
// opens a large in-panel preview so the user can see what's on the page.
// Selection mirrors into hidden floorplan_page_numbers[] inputs — all pages
// selected (the default) submits nothing, meaning "no constraint". Images
// and single-page PDFs keep the picker hidden.
export default class extends Controller {
  static targets = [
    "panel", "chips", "inputs", "count",
    "preview", "previewCanvas", "previewLabel", "previewToggle"
  ]

  // Swallows clicks inside the preview card so the backdrop handler doesn't
  // close it (wired as click->floorplan-pages#noop:stop).
  noop() {}

  connect() {
    this.selected = new Set()
    this.pdf = null
    this.previewPage = null
    this.fileListener = (event) => this.fileChanged(event)
    this.fileInput = this.element.querySelector("input[type='file']")
    if (this.fileInput) this.fileInput.addEventListener("change", this.fileListener)
  }

  disconnect() {
    if (this.fileInput) this.fileInput.removeEventListener("change", this.fileListener)
    this.destroyPdf()
  }

  async fileChanged(event) {
    const file = event.target.files && event.target.files[0]
    this.reset()
    if (!file || file.type !== "application/pdf") return

    try {
      const buffer = await file.arrayBuffer()
      this.pdf = await pdfjsLib.getDocument({ data: buffer }).promise
      if (this.pdf.numPages > 1) {
        await this.renderCards(this.pdf.numPages)
      } else {
        this.destroyPdf()
      }
    } catch (error) {
      // Unreadable PDF: leave the picker hidden — the server-side content
      // validations own rejecting the file itself.
      console.warn("[floorplan-pages] could not read PDF for page picking:", error)
      this.destroyPdf()
    }
  }

  async renderCards(pageCount) {
    this.chipsTarget.innerHTML = ""
    this.selected = new Set(Array.from({ length: pageCount }, (_, index) => index + 1))

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
      this.chipsTarget.appendChild(this.buildCard(pageNumber))
    }

    this.panelTarget.classList.remove("hidden")
    this.paint()

    // Thumbnails render after the cards exist so selection is usable at once.
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
      await this.renderThumbnail(pageNumber)
    }
  }

  buildCard(pageNumber) {
    const card = document.createElement("div")
    card.dataset.page = pageNumber
    card.className = "relative w-32 rounded-md border-2 overflow-hidden bg-white cursor-zoom-in"

    const canvas = document.createElement("canvas")
    canvas.className = "block w-full bg-gray-50"
    canvas.height = Math.round(THUMB_WIDTH * 1.4)
    canvas.width = THUMB_WIDTH
    card.appendChild(canvas)

    const footer = document.createElement("div")
    footer.className = "flex items-center justify-between px-2 py-1 text-xs font-medium text-gray-700 border-t border-gray-200 bg-gray-50"
    footer.innerHTML = `<span>Page ${pageNumber}</span>`
    card.appendChild(footer)

    // Corner tick: toggles selection without opening the preview.
    const tick = document.createElement("button")
    tick.type = "button"
    tick.dataset.role = "tick"
    tick.setAttribute("aria-label", `Use page ${pageNumber} for this floor`)
    tick.className = "absolute top-1.5 right-1.5 w-6 h-6 rounded-full border-2 flex items-center justify-center bg-white"
    tick.addEventListener("click", (event) => {
      event.stopPropagation()
      this.toggle(pageNumber)
    })
    card.appendChild(tick)

    // Clicking the card shows the large preview (TASK-86).
    card.addEventListener("click", () => this.openPreview(pageNumber))

    return card
  }

  async renderThumbnail(pageNumber) {
    if (!this.pdf) return
    try {
      const page = await this.pdf.getPage(pageNumber)
      const card = this.cardFor(pageNumber)
      if (!card) return
      const canvas = card.querySelector("canvas")
      const dpr = window.devicePixelRatio || 1
      const base = page.getViewport({ scale: 1 })
      const scale = (THUMB_WIDTH / base.width) * dpr
      const viewport = page.getViewport({ scale })

      canvas.width = Math.round(viewport.width)
      canvas.height = Math.round(viewport.height)
      canvas.style.width = `${THUMB_WIDTH}px`
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise
    } catch (error) {
      console.warn(`[floorplan-pages] thumbnail render failed for page ${pageNumber}:`, error)
    }
  }

  // ── Large preview ──────────────────────────────────────────────────────────

  async openPreview(pageNumber) {
    if (!this.pdf || !this.hasPreviewTarget) return
    this.previewPage = pageNumber

    this.previewLabelTarget.textContent = `Page ${pageNumber} of ${this.pdf.numPages}`
    this.paintPreviewToggle()
    this.previewTarget.classList.remove("hidden")

    try {
      const page = await this.pdf.getPage(pageNumber)
      const canvas = this.previewCanvasTarget
      const dpr = window.devicePixelRatio || 1
      const base = page.getViewport({ scale: 1 })
      const cssWidth = Math.min(PREVIEW_MAX, this.previewTarget.clientWidth - 32 || PREVIEW_MAX)
      const scale = (cssWidth / base.width) * dpr
      const viewport = page.getViewport({ scale })

      canvas.width = Math.round(viewport.width)
      canvas.height = Math.round(viewport.height)
      canvas.style.width = `${Math.round(viewport.width / dpr)}px`
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise
    } catch (error) {
      console.warn(`[floorplan-pages] preview render failed for page ${pageNumber}:`, error)
    }
  }

  closePreview() {
    this.previewPage = null
    if (this.hasPreviewTarget) this.previewTarget.classList.add("hidden")
  }

  togglePreviewPage() {
    if (this.previewPage) this.toggle(this.previewPage)
  }

  paintPreviewToggle() {
    if (!this.hasPreviewToggleTarget || !this.previewPage) return
    const on = this.selected.has(this.previewPage)
    this.previewToggleTarget.textContent = on ? "Don't use this page" : "Use this page"
    this.previewToggleTarget.className = on
      ? "btn-outline-gray-sm"
      : "btn-blue-sm"
  }

  // ── Selection ──────────────────────────────────────────────────────────────

  toggle(pageNumber) {
    if (this.selected.has(pageNumber)) {
      // Never allow an empty selection — a floor must own at least one page.
      if (this.selected.size === 1) return
      this.selected.delete(pageNumber)
    } else {
      this.selected.add(pageNumber)
    }
    this.paint()
    this.paintPreviewToggle()
  }

  paint() {
    const cards = this.chipsTarget.querySelectorAll("[data-page]")
    cards.forEach(card => {
      const on = this.selected.has(parseInt(card.dataset.page, 10))
      card.classList.toggle("border-blue-600", on)
      card.classList.toggle("border-gray-200", !on)
      card.classList.toggle("opacity-60", !on)

      const tick = card.querySelector("[data-role='tick']")
      tick.setAttribute("aria-pressed", on ? "true" : "false")
      tick.className = on
        ? "absolute top-1.5 right-1.5 w-6 h-6 rounded-full border-2 border-blue-600 bg-blue-600 text-white flex items-center justify-center"
        : "absolute top-1.5 right-1.5 w-6 h-6 rounded-full border-2 border-gray-300 bg-white text-transparent flex items-center justify-center"
      tick.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4" aria-hidden="true"><path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd"/></svg>'
    })

    this.inputsTarget.innerHTML = ""
    const all = cards.length
    // All pages selected = no constraint; submit nothing and store [].
    if (this.selected.size < all) {
      ;[...this.selected].sort((a, b) => a - b).forEach(pageNumber => {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "floorplan_page_numbers[]"
        input.value = pageNumber
        this.inputsTarget.appendChild(input)
      })
    }

    if (this.hasCountTarget) {
      this.countTarget.textContent = this.selected.size === all
        ? "All pages"
        : `${this.selected.size} of ${all} pages`
    }
  }

  cardFor(pageNumber) {
    return this.chipsTarget.querySelector(`[data-page="${pageNumber}"]`)
  }

  destroyPdf() {
    if (this.pdf) {
      this.pdf.destroy()
      this.pdf = null
    }
  }

  reset() {
    this.selected = new Set()
    this.previewPage = null
    this.destroyPdf()
    if (this.hasPanelTarget) this.panelTarget.classList.add("hidden")
    if (this.hasChipsTarget) this.chipsTarget.innerHTML = ""
    if (this.hasInputsTarget) this.inputsTarget.innerHTML = ""
    if (this.hasPreviewTarget) this.previewTarget.classList.add("hidden")
  }
}
